import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Calendar, ArrowRight } from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { SectionHeading } from './SectionHeading';

const Events = () => {
  const [filter, setFilter] = useState('All');
  const filters = ['All', 'Upcoming', 'Past'];
  const { data: events, loading } = useContent('events', []);

  const items = events || [];
  const filteredEvents = filter === 'All' ? items : items.filter((event) => event.status === filter.toLowerCase());

  if (!loading && items.length === 0) return null;

  return (
    <section id="events" className="py-24 bg-white" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4">
        <SectionHeading
          eyebrow="News & Events"
          title="What's"
          accent="Happening"
          subtitle="Stay updated on EWRF events, fundraisers, and community programmes."
        />

        <div className="flex items-center justify-center gap-3 mb-10">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-6 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider transition-all ${
                filter === f
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg scale-105'
                  : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-orange-300'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid gap-6">
          {filteredEvents.map((event) => {
            const isUpcoming = event.status === 'upcoming';
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            const resolveImg = (src) =>
              !src ? '' : src.startsWith('http') ? src : `${BACKEND_URL}${src}`;
            const handleAction = () => {
              if (event.action_url) {
                window.open(event.action_url, '_blank', 'noopener,noreferrer');
              }
            };
            return (
              <Card key={event.id} className="group overflow-hidden border-2 border-gray-100 hover:border-amber-200 hover:shadow-2xl transition-all">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row md:min-h-[220px]">
                    {/* Date column — standardised width & alignment */}
                    <div className={`w-full md:w-40 flex-shrink-0 p-6 flex md:flex-col items-center justify-center gap-3 md:gap-1 ${
                      isUpcoming
                        ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500'
                        : 'bg-gradient-to-br from-slate-500 to-slate-700'
                    } text-white`}>
                      <Calendar className="w-7 h-7 md:mb-2 opacity-80" />
                      <div className="text-4xl md:text-5xl font-black leading-none drop-shadow-lg tabular-nums">{event.date?.day}</div>
                      <div className="text-sm md:text-base font-bold uppercase tracking-widest">{event.date?.month}</div>
                      <div className="text-xs md:text-sm opacity-80">{event.date?.year}</div>
                    </div>

                    {/* Event image column — fixed 16:9 aspect on desktop for a uniform look */}
                    {event.image && (
                      <div
                        className="w-full md:w-80 md:aspect-auto md:min-h-full flex-shrink-0 bg-gray-100"
                        data-testid={`event-image-${event.id}`}
                      >
                        <div className="w-full aspect-video md:aspect-auto md:h-full">
                          <img
                            src={resolveImg(event.image)}
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    )}

                    {/* Content column */}
                    <div className="flex-1 p-6 md:p-8">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1">
                          <Badge
                            className={`mb-3 text-xs font-bold uppercase tracking-widest ${
                              isUpcoming
                                ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                : 'bg-slate-100 text-slate-600 border-slate-200'
                            }`}
                          >
                            {event.status}
                          </Badge>
                          <h3 className="text-xl md:text-2xl font-bold text-[#0A1628] mb-3 group-hover:text-orange-600 transition-colors">
                            {event.title}
                          </h3>
                          {event.location && (
                            <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                              <MapPin className="w-4 h-4 text-orange-500" />
                              <span>{event.location}</span>
                            </div>
                          )}
                          <p className="text-gray-600 leading-relaxed">{event.description}</p>
                        </div>
                        {event.action && event.action_url && (
                          <Button
                            onClick={handleAction}
                            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-5 font-bold uppercase tracking-wider shadow-lg whitespace-nowrap self-start"
                            data-testid={`event-action-${event.id}`}
                          >
                            {event.action}
                            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Events;
